import type { WorkitemTag } from '@rhythmic/db/types'
import { generateUuid, WorkitemTagSchema } from '@rhythmic/types'
import type { Transaction } from '@tanstack/vue-db'
import { createOptimisticAction } from '@tanstack/vue-db'
import { useRhythmicLogger } from '#imports'

import { getWorkitemTagsCollection } from '../../getCollections'

const logger = useRhythmicLogger('useTeamContext:createWorkitemTag')

export interface CreateWorkitemTagPayload {
  workitemId: string
  tagId: string
  workspaceId?: string | null
  eid?: string
}

export interface WorkitemTagMutationContext {
  teamId: string
  workspaceId?: string | null
}

function validateParams(payload: CreateWorkitemTagPayload, context: WorkitemTagMutationContext) {
  if (!payload.workitemId) throw new Error('Workitem identifier is required')

  if (!payload.tagId) throw new Error('Tag identifier is required')

  if (!context.teamId) throw new Error('Team context is not ready')

  return {
    workitemId: payload.workitemId,
    tagId: payload.tagId,
    teamId: context.teamId,
    workspaceId: payload.workspaceId ?? context.workspaceId ?? null,
    eid: payload.eid ?? generateUuid(),
  }
}

export default function handleCreateWorkitemTag(
  payload: CreateWorkitemTagPayload,
  context: WorkitemTagMutationContext,
): Transaction {
  const resolved = validateParams(payload, context)
  const workitemTagsCollection = getWorkitemTagsCollection(resolved.teamId)

  const action = createOptimisticAction<{
    payload: CreateWorkitemTagPayload
    context: WorkitemTagMutationContext
  }>({
    onMutate: () => {
      const record = WorkitemTagSchema.parse({
        eid: resolved.eid,
        tag_id: resolved.tagId,
        workitem_id: resolved.workitemId,
        team_id: resolved.teamId,
        workspace_id: resolved.workspaceId,
      })

      logger.debug('Optimistically inserting workitem tag', { record })
      workitemTagsCollection.insert(record)
    },
    mutationFn: async () => {
      const result = await $fetch<{ txid?: string | number }>(`/api/workitems/${resolved.workitemId}/tags` as string, {
        method: 'POST',
        body: {
          tag_id: resolved.tagId,
          team_id: resolved.teamId,
          workspace_id: resolved.workspaceId,
        },
      })

      if (result?.txid && workitemTagsCollection.utils?.awaitTxId)
        await workitemTagsCollection.utils.awaitTxId(Number.parseInt(result.txid.toString()))
    },
  })

  return action({ payload, context })
}
